import { Tabs, Row, Typography, Button, Spin, Image, Col, Input, List, Tooltip, Select, message } from "antd";
import { useEffect, useState, useRef } from "react";
import { useMediaQuery } from "react-responsive";
import HTTPClient from "../../utils/HTTPClient";
import { useNavigate, useSearchParams } from "react-router-dom";
import moment from "moment/moment";
import { MessageOutlined, FileImageOutlined, ExperimentOutlined, CloudUploadOutlined, DownloadOutlined, InfoCircleOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import {
    MinChatUiProvider,
    MainContainer,
    MessageInput,
    MessageContainer,
    MessageList,
    MessageHeader
} from "../../external_libs/chat_ui/index.tsx";

const { TextArea } = Input;

const AB_AI_Studio = () => {
    const navigate = useNavigate();
    const [aiStudioData, setAiStudioData] = useState(null);
    const [usage, setUsage] = useState(0);
    const [historyData, setHistoryData] = useState([]);
    const [selectedChat, setSelectedChat] = useState(null);
    const [selectedModel, setSelectedModel] = useState("gpt-4o");
    const [chatMessages, setChatMessages] = useState([]);
    const [imageRatio, setImageRatio] = useState("ASPECT_1_1");
    const [imageStyle, setImageStyle] = useState("GENERAL");
    const [imagePrompt, setImagePrompt] = useState("");
    const [imageUrl, setImageUrl] = useState("");
    const [imageLoading, setImageLoading] = useState(false);
    const [loading, setLoading] = useState(false);
    const [loadingChatResponse, setLoadingChatResponse] = useState(false);
    const [activeKey, setActiveKey] = useState("1");
    const [fileName, setFileName] = useState('');
    const [fullFilePath, setFullFilePath] = useState('');
    const [toggleScrollToBottom, setToggleScrollToBottom] = useState(false);

    const { t } = useTranslation();

    const isLargeScreen = useMediaQuery({
        query: "(min-width: 771px)",
    });

    const IMAGES_RATIOS = [
        "ASPECT_1_1",
        "ASPECT_10_16",
        "ASPECT_16_10",
        "ASPECT_9_16",
        "ASPECT_16_9",
        "ASPECT_3_2",
        "ASPECT_2_3",
        "ASPECT_4_3",
        "ASPECT_3_4",
        "ASPECT_1_3",
        "ASPECT_3_1"
    ]

    const IMAGES_STYLES = [
        "GENERAL",
        "REALISTIC",
        "DESIGN",
        "RENDER_3D",
        "ANIME"
    ]

    const fetch = async () => {
        setLoading(true);
        HTTPClient.get("/ab_ai_studio/").then((response) => {
            if (response?.data) {
                setAiStudioData(response.data);
                setHistoryData(response.data.history);
                if (selectedChat) {
                    const chat = response.data.chats.find(chat => chat._id === selectedChat);
                    if (chat) {
                        setChatMessages(chat.messages);
                    }
                }
                setUsage(response.data.usage);
            }
            setLoading(false);
        }).catch((error) => {
            if (error.response?.status === 402) {
                navigate("/home");
            }
            setLoading(false);
        });
    };

    useEffect(() => {
        fetch();
    }, []);

    const uploadFile = (file) => {
        const fileInput = document.createElement("input");
        fileInput.type = "file";
        fileInput.accept = "image/*";
        fileInput.click();

        fileInput.addEventListener("change", (event) => {
            const selectedFile = event.target.files[0];
            setFileName(selectedFile.name);
            setFullFilePath(selectedFile)
        });
    };

    const fetchUsage = async () => {
        const response = await HTTPClient.get("/ab_ai_studio");
        if (response?.data) {
            setUsage(response.data.usage);
        }
    };

    const fetchHistory = async () => {
        const response = await HTTPClient.get("/ab_ai_studio");
        if (response?.data) {
            setHistoryData(response.data.history);
        }
    };

    const handleNewMessage = (text) => {
        setLoadingChatResponse(true);
        setChatMessages([...chatMessages, { text, user: { id: "user", name: "User" } }]);
        setToggleScrollToBottom(!toggleScrollToBottom);
        if (fileName) {
            const formData = new FormData();
            formData.append("file", fullFilePath);
            formData.append("prompt", text);
            formData.append("chat_id", selectedChat);
            formData.append("model", selectedModel);
            HTTPClient.post("/ab_ai_studio/chat/attachment", formData, { headers: { "Content-Type": "multipart/form-data" } }).then((response) => {
                if (response?.data) {
                    setChatMessages(response.data.messages);
                    setToggleScrollToBottom(!toggleScrollToBottom);
                    setSelectedChat(response.data.chat_id);
                    setFileName('');
                    setFullFilePath('');
                    fetchUsage();
                }
            }).catch((error) => {
                console.log(error);
            }).finally(() => {
                setLoadingChatResponse(false);
            });
            return;
        }
        if (selectedChat) {
            HTTPClient.post("/ab_ai_studio/chat", { prompt: text, chat_id: selectedChat, model: null }).then((response) => {
                if (response?.data) {
                    setChatMessages(response.data.messages);
                    setToggleScrollToBottom(!toggleScrollToBottom);
                    fetchUsage();
                }
            }).catch((error) => {
                console.log(error);
            }).finally(() => {
                setLoadingChatResponse(false);
            });
        } else {
            HTTPClient.post("/ab_ai_studio/chat", { prompt: text, model: selectedModel, chat_id: null }).then((response) => {
                if (response?.data) {
                    setChatMessages(response.data.messages);
                    setToggleScrollToBottom(!toggleScrollToBottom);
                    setSelectedChat(response.data.chat_id);
                    fetchUsage();
                    fetchHistory();
                }
            }).catch((error) => {
                console.log(error);
            }).finally(() => {
                setLoadingChatResponse(false);
            });
        }
    }

    const createImage = () => {
        setImageLoading(true);
        HTTPClient.post("/ab_ai_studio/image", { prompt: imagePrompt, ratio: imageRatio, style: imageStyle }).then((response) => {
            if (response?.data) {
                setImageUrl(response.data.url);
            }
            fetch();
            setImageLoading(false);
        }).catch((error) => {
            setImageLoading(false);
        });
    }

    const handleHistoryClick = (item) => {
        if (item.url) {
            setImageUrl(item.url);
            setImagePrompt(item.full_text);
            setImageRatio(item.ratio);
            setImageStyle(item.style);
            setActiveKey("1");
            console.log(item);
        } else {
            setSelectedChat(item._id);
            const chat = aiStudioData.chats.find(chat => chat._id === item._id);
            if (chat) {
                setChatMessages(chat.messages);
            }
            setActiveKey("1");
        }
    }

    const importToWebsite = () => {
        setImageLoading(true);
        HTTPClient.post("/ab_website/import_image", { image_url: imageUrl }).then((response) => {
            message.success(t("ab_ai_studio.main.images.import_success"));
        }).catch((error) => {
            message.error(t("ab_ai_studio.main.images.import_error"));
        }).finally(() => {
            setImageLoading(false);
        });
    }

    const handleDownloadImage = () => {
        setImageLoading(true);
        const link = document.createElement("a");
        link.href = imageUrl;
        link.download = "image.png";
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        setImageLoading(false);
    };

    const cleanChat = () => {
        setChatMessages([]);
        setSelectedChat(null);
    }

    const OLD_chatComponent = (<>
        <Row style={{ display: "flex", justifyContent: "center", alignItems: "center" }}>
            <Button onClick={() => cleanChat()} type="primary">{t("ab_ai_studio.main.chat.new")}</Button>
        </Row>
        <Row style={{ maxHeight: "450px", marginTop: "25px" }}>
            <MinChatUiProvider theme="#8476f5">
                <MainContainer style={{ height: "400px" }} render>
                    <MessageContainer>
                        <MessageList
                            currentUserId="user"
                            showTypingIndicator={loadingChatResponse}
                            typingIndicatorContent="Appbox AI is thinking..."
                            toggleScrollToBottom={toggleScrollToBottom}
                            messages={[
                                {
                                    text: t("ab_ai_studio.main.chat.welcome"),
                                    user: {
                                        id: "ai",
                                        name: "AI",
                                    },
                                },
                                ...chatMessages
                            ]}
                        />
                        {fileName && <div style={{ backgroundColor: "#f3f4f6", width: "98.5%" }}>{fileName}</div>}
                        <MessageInput placeholder={t("ab_ai_studio.main.chat.input_placeholder")} showAttachButton={true} onSendMessage={(text) => handleNewMessage(text)} onAttachClick={uploadFile} />
                    </MessageContainer>
                </MainContainer>
            </MinChatUiProvider>
        </Row></>
    )

    const chatComponent = (
        <Row style={{ maxHeight: "450px", marginTop: "10px" }}>
            <Row style={{ display: "flex", justifyContent: "center", alignItems: "center", marginBottom: "15px", width: "100%" }}>
                <Typography.Text style={{ fontSize: "18px" }}>{t("ab_ai_studio.main.chat.video")}</Typography.Text>
            </Row>
            <Row style={{ display: "flex", justifyContent: "center", alignItems: "center", marginBottom: "15px", width: "100%" }}>
                <iframe
                    id="ytplayer"
                    type="text/html"
                    width="620"
                    height="350"
                    src="https://www.youtube.com/embed/k5J7aQs_CBY"
                    allowFullScreen
                ></iframe>
            </Row>
        </Row>
    )

    const imageComponent = (
        <Row style={{ maxHeight: "450px", marginTop: "25px" }}>
            <Row style={{ display: "flex", justifyContent: "center", alignItems: "center", width: "100%" }}>
                <Col span={16}>
                    <TextArea
                        prefix={<MessageOutlined />}
                        placeholder={t("ab_ai_studio.main.images.input_placeholder")}
                        autoSize={{
                            minRows: 3,
                            maxRows: 6,
                        }}
                        style={{ width: "100%" }}
                        value={imagePrompt}
                        onChange={(e) => setImagePrompt(e.target.value)}
                    />
                </Col>
                <Col span={5} style={{ display: "flex" }}>
                    <Select value={imageRatio} style={{ width: "100%", marginLeft: "5px", marginRight: "5px" }} onChange={(value) => setImageRatio(value)}>
                        {IMAGES_RATIOS.map(ratio => <Select.Option value={ratio}>{ratio.replace("ASPECT_", " ").replace("_", ":")}</Select.Option>)}
                    </Select>
                    <Select value={imageStyle} style={{ width: "100%", marginRight: "5px" }} onChange={(value) => setImageStyle(value)}>
                        {IMAGES_STYLES.map(style => <Select.Option value={style}>{style}</Select.Option>)}
                    </Select>
                </Col>
                <Col span={3} style={{ paddingLeft: "10px" }}>
                    <Button onClick={() => createImage()} loading={imageLoading} icon={<ExperimentOutlined />} size="large" type="primary" style={{ marginLeft: "10px" }}></Button>
                </Col>
            </Row>
            {imageUrl &&
                <><Row style={{ marginTop: "35px", display: "flex", justifyContent: "center", alignContent: "center", width: "100%" }}>
                    <Image src={imageUrl} style={{ maxWidth: "200px", maxHeight: "200px" }} />
                </Row>
                    <Row style={{ marginTop: "35px", display: "flex", justifyContent: "center", alignContent: "center", width: "100%" }}>
                        <Button loading={imageLoading} onClick={handleDownloadImage} size="large" type="primary" icon={<DownloadOutlined />}>{t("ab_ai_studio.main.images.download")}</Button>
                    </Row>
                    <Row style={{ marginTop: "20px", display: "flex", justifyContent: "center", alignContent: "center", width: "100%" }}>
                        <Button loading={imageLoading} onClick={importToWebsite} size="large" type="primary" icon={<CloudUploadOutlined />}>{t("ab_ai_studio.main.images.import")}</Button>
                    </Row>
                </>}

        </Row>
    )


    return (<>
        <Row style={{ backgroundColor: "white", width: "100%", height: isLargeScreen ? "134px" : "180px", padding: "27px 34px" }}>
            <Row>
                <Typography.Text style={{ fontSize: "27px", fontWeight: "bold" }}>
                    {t("ab_ai_studio.title")}
                </Typography.Text>
            </Row>
            <Row style={{ width: "100%", marginTop: "10px" }}>
                <Typography.Text style={{ fontSize: "20px" }}>{t("ab_ai_studio.subtitle")}</Typography.Text>
            </Row>
        </Row>
        <Row style={{ padding: isLargeScreen ? "27px 34px" : "10px 10px" }}>

            {loading || !aiStudioData ? (
                <div
                    style={{
                        width: "100%",
                        minHeight: "20vh",
                        overflow: "auto",
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                    }}>
                    <Spin />
                </div>
            ) : (
                <>
                    <Row style={{ width: "100%" }}>
                        <Col span={isLargeScreen ? 17 : 24}>
                            <div style={{ boxShadow: "0px 4px 4px rgba(0, 0, 0, 0.25)", backgroundColor: "white", width: "100%", height: "100%", padding: "22px 27px", borderRadius: "14px" }}>
                                <Row style={{ display: "flex", justifyContent: "center", alignItems: "center" }}><img src={"/AI.png"} style={{ width: "100px", height: "100px" }} /></Row>
                                <Row style={{ display: "flex", justifyContent: "center", alignItems: "center" }}>
                                    <Typography.Text style={{ fontSize: "24px", fontWeight: "bold" }}>{t("ab_ai_studio.main.title")} {aiStudioData?.username}</Typography.Text>
                                </Row>
                                <Row style={{ display: "flex", justifyContent: "center", alignItems: "center" }}>
                                    <Typography.Text style={{ fontSize: "18px" }}>{t("ab_ai_studio.main.subtitle")}</Typography.Text>
                                </Row>
                                <Tabs activeKey={activeKey} centered size="large" onChange={(key) => setActiveKey(key)}>
                                    <Tabs.TabPane
                                        tab={
                                            <Tooltip title={t("ab_ai_studio.main.tab_images_tooltip")}>
                                                <span>
                                                    <FileImageOutlined />
                                                    {t("ab_ai_studio.main.tab_images_label")}
                                                </span>
                                            </Tooltip>
                                        }
                                        key="1"
                                    >
                                        {imageComponent}
                                    </Tabs.TabPane>
                                    <Tabs.TabPane
                                        tab={
                                            <Tooltip title={t("ab_ai_studio.main.tab_chat_tooltip")}>
                                                <span>
                                                    <MessageOutlined />
                                                    {t("ab_ai_studio.main.tab_chat_label")}
                                                </span>
                                            </Tooltip>
                                        }
                                        key="2"
                                    >
                                        {chatComponent}
                                    </Tabs.TabPane>
                                </Tabs>
                            </div>
                        </Col>
                        {isLargeScreen && <Col span={1}></Col>}
                        <Col span={isLargeScreen ? 6 : 24} style={{ marginTop: isLargeScreen ? "0px" : "30px", display: "flex", flexDirection: "column" }}>
                            <Row style={{ boxShadow: "0px 4px 4px rgba(0, 0, 0, 0.25)", backgroundColor: "white", width: "100%", height: "130px", padding: "18px 22px", borderRadius: "14px" }}>
                                <Col span={14} style={{ display: "flex", justifyContent: "center", flexDirection: "column" }}>
                                    <Row>
                                        <Typography.Text style={{ fontSize: "18px", fontWeight: "bold" }}>{t("ab_ai_studio.usage.title")}</Typography.Text>
                                        <Tooltip title={t("ab_ai_studio.usage.tooltip")}><InfoCircleOutlined style={{ marginLeft: "10px" }} /></Tooltip>
                                    </Row>
                                </Col>
                                <Col span={10} style={{ display: "flex", justifyContent: "center", flexDirection: "column", alignItems: "center" }}>
                                    <Row><Typography.Text style={{ fontSize: "24px", fontWeight: "bold" }}>{usage}/{aiStudioData?.limit}</Typography.Text></Row>
                                    {/*<Row><RocketOutlined /><Typography.Text style={{ fontSize: "12px", fontWeight: "bold" }}>{t("ab_ai_studio.usage.upgrade")}</Typography.Text></Row>*/}
                                </Col>
                            </Row>
                            <Row style={{ boxShadow: "0px 4px 4px rgba(0, 0, 0, 0.25)", backgroundColor: "white", width: "100%", marginTop: "25px", maxHeight: "400px", padding: "22px 27px", borderRadius: "14px" }}>
                                <Row style={{ width: "100%" }}>
                                    <Typography.Text style={{ fontSize: "18px", fontWeight: "bold" }}>{t("ab_ai_studio.history.title")}</Typography.Text>
                                </Row>
                                <Row style={{ width: "100%", marginTop: "10px" }}>
                                    {historyData.length === 0 ? <Typography.Text>{t("ab_ai_studio.history.empty")}</Typography.Text> :
                                        <List
                                            pagination={{
                                                pageSize: 5,
                                            }}
                                            itemLayout="horizontal"
                                            style={{ width: "100%" }}
                                            dataSource={historyData}
                                            renderItem={(item, index) => (
                                                <List.Item onClick={() => handleHistoryClick(item)} style={{ cursor: "pointer" }}>
                                                    <List.Item.Meta
                                                        avatar={item._id ? <MessageOutlined /> : <FileImageOutlined />}
                                                        description={item.text}
                                                    />
                                                </List.Item>
                                            )}
                                        />}
                                </Row>
                            </Row>

                        </Col>
                    </Row>

                </>
            )}

        </Row></>
    );
};

export { AB_AI_Studio };
